import { Node, mergeAttributes } from "@tiptap/core"
import { Plugin, Selection, TextSelection } from "@tiptap/pm/state"
import type { EditorState, Transaction } from "@tiptap/pm/state"
import { Mapping, insertPoint } from "@tiptap/pm/transform"
import { isHistoryTransaction } from "@tiptap/pm/history"
import type { Fragment, Node as ProseMirrorNode } from "@tiptap/pm/model"
import { Decoration, DecorationSet } from "@tiptap/pm/view"
import type { EditorView } from "@tiptap/pm/view"

export const REPLACE_PROMPT_DOCUMENT = "replacePromptDocument"

/**
 * 把与 [from, to] 相交（或包含该位置）的变量框还原为普通内容，保留框上的下划线。
 * 带 REPLACE_PROMPT_DOCUMENT 标记，避免被“误删变量框自动恢复”的逻辑还原。
 * 没有可还原的变量框时返回 null。
 */
export const unwrapPromptFields = (
  state: EditorState,
  from: number,
  to: number,
): Transaction | null => {
  const targets: { node: ProseMirrorNode; position: number }[] = []
  state.doc.nodesBetween(from, to, (node, position) => {
    if (node.type.name !== "promptField") return
    targets.push({ node, position })
    return false
  })
  if (!targets.length) return null
  const tr = state.tr
  // 从后往前替换，前面的位置不受影响
  for (let index = targets.length - 1; index >= 0; index--) {
    const { node, position } = targets[index]
    const children: ProseMirrorNode[] = []
    node.content.forEach((child) => children.push(child.mark(node.marks)))
    tr.replaceWith(position, position + node.nodeSize, children)
  }
  return tr.setMeta(REPLACE_PROMPT_DOCUMENT, true)
}

// Chrome treats the caret before a field and before its first character as the
// same DOM position. A zero-width atom outside each edge keeps them distinct.
const createSeparator = (view: EditorView) => {
  const separator = view.dom.ownerDocument.createElement("img")
  separator.className = "ProseMirror-separator"
  return separator
}

type PromptFieldExtensionOptions = {
  // 变量框被整体删除时是否自动补回：用户侧必须保留模板变量，Staff 配置时允许删除
  restoreDeleted?: boolean
}

export const createPromptFieldExtension = (
  className: string,
  { restoreDeleted = true }: PromptFieldExtensionOptions = {},
) =>
  Node.create({
    name: "promptField",
    group: "inline",
    inline: true,
    content: "(text | hardBreak | resourceMention)*",
    marks: "",
    selectable: false,
    defining: true,
    addAttributes() {
      return {
        name: {
          default: "",
          parseHTML: (element) => element.getAttribute("data-prompt-field"),
          renderHTML: (attrs) => ({ "data-prompt-field": attrs.name }),
        },
        label: {
          default: "待填写内容",
          parseHTML: (element) => element.getAttribute("data-label"),
          renderHTML: (attrs) => ({ "data-label": attrs.label }),
        },
        required: {
          default: false,
          parseHTML: (element) =>
            element.getAttribute("data-required") === "true",
          renderHTML: (attrs) => ({ "data-required": String(attrs.required) }),
        },
        maxLength: {
          default: 0,
          parseHTML: (element) =>
            Number(element.getAttribute("data-max-length")) || 0,
          renderHTML: (attrs) => ({ "data-max-length": attrs.maxLength }),
        },
      }
    },
    parseHTML() {
      return [{ tag: "span[data-prompt-field]" }]
    },
    renderHTML({ HTMLAttributes }) {
      return ["span", mergeAttributes(HTMLAttributes, { class: className }), 0]
    },
    addKeyboardShortcuts() {
      const segmenter = new Intl.Segmenter(undefined, {
        granularity: "grapheme",
      })
      const moveFieldBoundary = (direction: -1 | 1) => {
        const { state, view } = this.editor
        let { $from, empty } = state.selection
        // Native arrow moves can precede ProseMirror's selectionchange update.
        const selection = view.dom.ownerDocument.getSelection()
        if (selection?.anchorNode && view.dom.contains(selection.anchorNode)) {
          empty = selection.isCollapsed
          $from = state.doc.resolve(
            view.posAtDOM(selection.anchorNode, selection.anchorOffset),
          )
        }
        if (!view.editable || view.composing || !empty) return false

        let position: number
        if ($from.parent.type.name === this.name) {
          const size = $from.parent.content.size
          const edge = direction < 0 ? 0 : size
          if ($from.parentOffset === edge) {
            position = direction < 0 ? $from.before() : $from.after()
          } else {
            const node =
              direction < 0 ? $from.parent.firstChild : $from.parent.lastChild
            if (!node?.isText) return false
            const segments = Array.from(segmenter.segment(node.textContent))
            const length = (direction < 0 ? segments[0] : segments.at(-1))
              .segment.length
            if ($from.parentOffset !== (direction < 0 ? length : size - length))
              return false
            // Native arrows skip zero-width caret stops at the field edges.
            position = direction < 0 ? $from.start() : $from.end()
          }
        } else {
          const node = direction < 0 ? $from.nodeBefore : $from.nodeAfter
          if (node?.type.name !== this.name) return false
          position = $from.pos + direction
        }
        view.dispatch(
          state.tr
            .setSelection(TextSelection.create(state.doc, position))
            .scrollIntoView(),
        )
        return true
      }
      const leaveEmptyField = (direction: -1 | 1) => {
        const { state, view } = this.editor
        const { $from, empty } = state.selection
        if (
          !view.editable ||
          !empty ||
          $from.parent.type.name !== this.name ||
          $from.parent.content.size
        )
          return false
        const position = direction < 0 ? $from.before() : $from.after()
        view.dispatch(
          state.tr.setSelection(TextSelection.create(state.doc, position)),
        )
        return true
      }
      return {
        ArrowLeft: () => moveFieldBoundary(-1),
        ArrowRight: () => moveFieldBoundary(1),
        Enter: () =>
          this.editor.isActive(this.name) &&
          this.editor.commands.setHardBreak(),
        Backspace: () => leaveEmptyField(-1),
        Delete: () => leaveEmptyField(1),
      }
    },
    addProseMirrorPlugins() {
      return [
        new Plugin({
          // DOM composition can invoke fallback Backspace commands. A no-op
          // command must not reset the native selection and commit the IME early.
          filterTransaction: (tr) =>
            !this.editor.view.composing ||
            tr.docChanged ||
            tr.selectionSet ||
            tr.storedMarksSet ||
            !tr.isGeneric,
          appendTransaction: (transactions, oldState, newState) => {
            // History includes wrapper repairs and synchronized values. Broad replace
            // steps must not restore them a second time at remapped positions.
            if (
              !transactions.some((tr) => tr.docChanged) ||
              transactions.some(
                (tr) =>
                  tr.getMeta(REPLACE_PROMPT_DOCUMENT) ||
                  isHistoryTransaction(tr),
              )
            )
              return null
            const mapping = new Mapping()
            transactions.forEach((tr) => mapping.appendMapping(tr.mapping))
            const tr = newState.tr
            const changes = new Map<string, Fragment>()
            const active = oldState.selection.$from.parent
            const rememberChange = (
              name: string,
              content: Fragment,
              selected: boolean,
            ) => {
              if (!changes.has(name) || selected) changes.set(name, content)
            }
            const survivors = new Set<number>()
            const lost: { node: typeof active; position: number }[] = []
            oldState.doc.descendants((node, position) => {
              if (node.type.name !== this.name) return
              const start = mapping.mapResult(position, 1)
              const end = mapping.mapResult(position + node.nodeSize, -1)
              const current = newState.doc.nodeAt(start.pos)
              if (
                // deletedAfter also flags insertion at this exact boundary;
                // deleted respects the association used to map the wrapper.
                !start.deleted &&
                !end.deleted &&
                current?.type === node.type &&
                current.attrs.name === node.attrs.name &&
                start.pos + current.nodeSize === end.pos
              ) {
                survivors.add(start.pos)
                if (!current.content.eq(node.content))
                  rememberChange(
                    node.attrs.name,
                    current.content,
                    node === active,
                  )
              } else {
                lost.push({ node, position })
              }
              return false
            })
            const claimed = new Set<number>()
            lost.forEach(({ node, position }) => {
              // Native edits the beforeinput handler leaves to the browser (e.g.
              // a selection crossing the field edge, more common on Windows) are
              // re-parsed from the DOM and can replace the wrapper with a new one.
              // Reuse that replacement instead of restoring a duplicate field.
              const from = mapping.map(position, -1)
              const to = Math.max(
                mapping.map(position + node.nodeSize, 1),
                from + 1,
              )
              let replacement: { node: typeof node; position: number } | null =
                null
              newState.doc.nodesBetween(
                from,
                Math.min(to, newState.doc.content.size),
                (candidate, candidatePosition) => {
                  if (replacement) return false
                  if (
                    candidate.type === node.type &&
                    candidate.attrs.name === node.attrs.name &&
                    !survivors.has(candidatePosition) &&
                    !claimed.has(candidatePosition)
                  ) {
                    replacement = {
                      node: candidate,
                      position: candidatePosition,
                    }
                    return false
                  }
                },
              )
              if (replacement) {
                const { node: current, position: currentPosition } = replacement
                claimed.add(currentPosition)
                rememberChange(
                  node.attrs.name,
                  current.content,
                  node === active,
                )
                return
              }
              if (!restoreDeleted) return
              // A range edit may erase the wrapper. Restore an empty field at
              // its mapped position, in the same history event as the edit.
              const start = mapping.mapResult(position, 1)
              const target = tr.mapping.map(start.pos)
              let $pos = tr.doc.resolve(target)
              if (!$pos.parent.inlineContent) $pos = Selection.near($pos).$from
              for (let depth = $pos.depth; depth > 0; depth--) {
                if ($pos.node(depth).type.name === this.name) {
                  $pos = tr.doc.resolve($pos.after(depth))
                  break
                }
              }
              const point = insertPoint(tr.doc, $pos.pos, node.type)
              if (point !== null) {
                const empty = node.type.create(node.attrs)
                tr.insert(point, empty)
                rememberChange(node.attrs.name, empty.content, node === active)
              }
            })
            // Share content by variable name while keeping each occurrence's
            // wrapper, metadata and document position independent.
            const start = tr.steps.length
            tr.doc.descendants((node, position) => {
              if (node.type.name !== this.name) return
              const content = changes.get(node.attrs.name)
              if (content && !node.content.eq(content)) {
                const from = tr.mapping.slice(start).map(position + 1)
                tr.replaceWith(from, from + node.content.size, content)
              }
              return false
            })
            return tr.docChanged ? tr : null
          },
          props: {
            handleDOMEvents: {
              beforeinput: (view, event) => {
                if (
                  !view.editable ||
                  view.composing ||
                  event.isComposing ||
                  !event.cancelable
                )
                  return false
                const { state } = view
                const { $from, $to } = state.selection
                if (
                  !$from.sameParent($to) ||
                  $from.parent.type.name !== "promptField"
                )
                  return false
                // At inline boundaries Chrome can place the native caret
                // outside the field, especially after a trailing line break.
                if (event.inputType === "insertText" && event.data) {
                  event.preventDefault()
                  view.dispatch(
                    state.tr.insertText(event.data).scrollIntoView(),
                  )
                  return true
                }
                if (
                  !event.inputType.startsWith("delete") ||
                  !$from.parent.content.size
                )
                  return false
                const [range] = event.getTargetRanges()
                if (!range) return false
                const from = view.posAtDOM(
                  range.startContainer,
                  range.startOffset,
                )
                const to = view.posAtDOM(range.endContainer, range.endOffset)
                if (from < $from.start() || to > $from.end() || from >= to)
                  return false
                // Native deletion at the first character can leave the caret
                // outside the inline wrapper. Apply all in-field deletions to
                // the model and explicitly keep the caret inside, even when empty.
                event.preventDefault()
                const tr = state.tr.delete(from, to)
                view.dispatch(
                  tr
                    .setSelection(TextSelection.create(tr.doc, from))
                    .scrollIntoView(),
                )
                return true
              },
            },
            handleClickOn: (
              view,
              position,
              node,
              nodePosition,
              _event,
              direct,
            ) => {
              if (!view.editable || !direct || node.type.name !== "promptField")
                return false
              view.dispatch(
                view.state.tr.setSelection(
                  TextSelection.create(
                    view.state.doc,
                    Math.max(
                      nodePosition + 1,
                      Math.min(position, nodePosition + node.nodeSize - 1),
                    ),
                  ),
                ),
              )
              view.focus()
              // Chrome treats the outside edge as an equivalent DOM position.
              // A click on the field must put the native caret inside it too.
              const selection = view.dom.ownerDocument.getSelection()
              if (selection) {
                const caret = view.domAtPos(view.state.selection.from)
                selection.collapse(caret.node, caret.offset)
              }
              return true
            },
            decorations: (state) => {
              const decorations: Decoration[] = []
              state.doc.descendants((node, position) => {
                if (node.type.name !== "promptField") return
                const active =
                  state.selection.from > position &&
                  state.selection.to < position + node.nodeSize
                // Keep the outside carets beyond the separators: side 1 before
                // the field and side -1 after it.
                decorations.push(
                  Decoration.widget(position, createSeparator, {
                    side: 1,
                    key: "promptFieldBefore",
                  }),
                  Decoration.widget(position + node.nodeSize, createSeparator, {
                    side: -1,
                    key: "promptFieldAfter",
                  }),
                  Decoration.node(position, position + node.nodeSize, {
                    "data-active": String(active),
                  }),
                )
              })
              return DecorationSet.create(state.doc, decorations)
            },
          },
        }),
      ]
    },
  })
